import { useMutation } from "@tanstack/react-query";

import type {
    MonitorAndProtectModelCampaignsCampaignListItemModel,
    MonitorAndProtectModelCampaignsCampaignModel,
} from "@similarweb/pro-api-client";

import { useToasts } from "app/common/hooks/useToasts";
import { apiClient } from "app/modules/api-client";
import {
    MONITOR_AND_PROTECT_GET_CAMPAIGN_KEY,
    MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY,
} from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { TRADEMARKS_ADS_DAILY_CREATE_QUERY_KEY } from "app/pages/digital-marketing/monitor-and-protect/services/api/trademarks";
import { campaignAssetTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/asset-management";
import { MNP_LIMITS_QUERY_KEY } from "app/pages/digital-marketing/monitor-and-protect/shared/components/limits/use-limits";
import queryClient from "single-spa/react-app/query-client";

// A freshly created campaign is not consistently available across endpoints right
// away (backend read-after-write lag). The detail endpoint is the cheapest readiness
// signal: once it responds, the campaign's reports are ready too, so we poll it with
// increasing backoff before navigating.
const POLL_BASE_DELAY_MS = 300;
const POLL_MAX_ATTEMPTS = 6;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const waitForCampaignReady = async (
    campaignId: string,
): Promise<MonitorAndProtectModelCampaignsCampaignModel | null> => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        try {
            return await apiClient.MonitorAndProtectMaster.monitorandprotectCampaignsDetail(campaignId);
        } catch {
            if (attempt < POLL_MAX_ATTEMPTS - 1) {
                await delay(POLL_BASE_DELAY_MS * 2 ** attempt);
            }
        }
    }
    return null;
};

const toCampaignListItem = (
    campaign: MonitorAndProtectModelCampaignsCampaignModel,
): MonitorAndProtectModelCampaignsCampaignListItemModel => ({
    assetPermissions: campaign.assetPermissions,
    createdAt: campaign.createdAt,
    domain: campaign.domain,
    id: campaign.id,
    keywords: campaign.keywords,
    keywordsCount: campaign.keywords?.length ?? 0,
    lastUpdated: campaign.lastUpdated,
    locations: campaign.locations,
    name: campaign.name,
    type: campaign.type,
});

// The list endpoint can keep lagging after detail is already ready, so a plain
// refetch could overwrite the seeded campaign with a stale list and re-trigger the
// dialog bug. Instead, poll in the background and only commit a server list once it
// actually contains the new campaign; otherwise the seed stays until it converges.
const reconcileCampaignList = async (campaignId: string): Promise<void> => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        try {
            const list = await apiClient.MonitorAndProtectMaster.monitorandprotectCampaignsList({});
            if (list?.some((campaign) => campaign.id === campaignId)) {
                queryClient.setQueryData(MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY, list);
                return;
            }
        } catch {
            // ignore and retry
        }
        if (attempt < POLL_MAX_ATTEMPTS - 1) {
            await delay(POLL_BASE_DELAY_MS * 2 ** attempt);
        }
    }
};

const seedCampaignCaches = (campaign: MonitorAndProtectModelCampaignsCampaignModel): void => {
    queryClient.setQueryData([...MONITOR_AND_PROTECT_GET_CAMPAIGN_KEY, campaign.id], campaign);
    queryClient.setQueryData<MonitorAndProtectModelCampaignsCampaignListItemModel[]>(
        MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY,
        (currentList = []) =>
            currentList.some((listItem) => listItem.id === campaign.id)
                ? currentList
                : [...currentList, toCampaignListItem(campaign)],
    );
};

interface IUseCreateCampaignMutationProps {
    selectedDomain: string;
    keywords: string[];
    locations: { countryCode: number }[];
    campaignName: string;
    onSuccess?: (campaign: MonitorAndProtectModelCampaignsCampaignModel) => void;
}

export const useCreateCampaignMutation = ({
    selectedDomain,
    keywords,
    locations,
    campaignName,
    onSuccess,
}: IUseCreateCampaignMutationProps) => {
    const { showErrorToast } = useToasts();

    return useMutation({
        mutationKey: ["createCampaign", selectedDomain],
        mutationFn: () => {
            return apiClient.MonitorAndProtectMaster.monitorandprotectCampaignsCreate({
                domain: selectedDomain,
                keywords,
                locations,
                name: campaignName,
            });
        },
        onSuccess: async (data) => {
            campaignAssetTracking.onCreated({
                id: data?.id,
                name: data?.name ?? campaignName,
                keywordCount: keywords.length,
            });

            // Keep the wizard in its loading state until the campaign is ready, then
            // seed the caches so the selector and reports resolve it without waiting
            // for the lagging list endpoint.
            const readyCampaign = (await waitForCampaignReady(data.id)) ?? data;
            seedCampaignCaches(readyCampaign);

            onSuccess?.(data);

            void reconcileCampaignList(data.id);
            void queryClient.invalidateQueries({
                queryKey: MNP_LIMITS_QUERY_KEY,
            });
            void queryClient.invalidateQueries({
                queryKey: TRADEMARKS_ADS_DAILY_CREATE_QUERY_KEY,
            });
        },
        onError: () => {
            showErrorToast("Failed to create campaign. Please try again.");
        },
    });
};
